// SPDX-License-Identifier: AGPL-3.0-or-later
// Native assistant blocks shared by Home, mobile Ask, Cmd+K Ask and the full Assistant DM.

import {AssistantBriefCard} from '@app/features/konvo_assistant/components/AssistantBriefCard';
import {AssistantChoice} from '@app/features/konvo_assistant/components/AssistantChoice';
import {AssistantFindings} from '@app/features/konvo_assistant/components/AssistantFindings';
import {AssistantItemCard} from '@app/features/konvo_assistant/components/AssistantItemCard';
import styles from '@app/features/konvo_assistant/components/AssistantRichReply.module.css';
import {AssistantSourceCards} from '@app/features/konvo_assistant/components/AssistantSourceCards';
import {KonvoCardHead} from '@app/features/konvo_assistant/components/card/KonvoCardHead';
import {
	clockMinutes,
	rangeMinutes,
	readableDuration,
} from '@app/features/konvo_assistant/state/AssistantRichReplyModel';
import {SafeMarkdown} from '@app/features/messaging/components/markdown';
import {CalendarBlankIcon, CheckSquareIcon, ClockIcon, SparkleIcon} from '@app/features/ui/icons/Icons';
import type {
	AssistantCarouselBlock,
	AssistantMeetingBlock,
	AssistantResponse,
	AssistantResponseAction,
	AssistantSourceItem,
	AssistantTimelineBlock,
} from '@fluxer/schema/src/domains/konvo/AssistantResponse';
import {useLingui} from '@lingui/react/macro';
import {lazy, Suspense} from 'react';

// KONVO: the Agents card brings the router and the Agents bits; load it only when one is on screen.
const AssistantAgentsBlock = lazy(() =>
	import('@app/features/konvo_agents/components/AssistantAgentsBlock').then((module) => ({
		default: module.AssistantAgentsBlock,
	})),
);

// KONVO: the plan usage card (show_agent_usage, usage warnings), loaded the same way.
const AssistantAgentUsageBlock = lazy(() =>
	import('@app/features/konvo_agents/components/AssistantAgentUsageBlock').then((module) => ({
		default: module.AssistantAgentUsageBlock,
	})),
);

// KONVO: the agents digest card (weekday mornings, "what did my agents do?"), loaded the same way.
const AssistantAgentsDigestBlock = lazy(() =>
	import('@app/features/konvo_agents/components/AssistantAgentsDigestBlock').then((module) => ({
		default: module.AssistantAgentsDigestBlock,
	})),
);

// KONVO: the end-of-day wrap-up card brings the router and the corner: loaded the same way.
const WrapUpCard = lazy(() =>
	import('@app/features/konvo_wrapup/components/WrapUpCard').then((module) => ({default: module.WrapUpCard})),
);

// Dashboards bring a chart library and their own store: load them only when one is on screen.
const DashboardBlockCard = lazy(() =>
	import('@app/features/konvo_dashboards/components/DashboardCard').then((module) => ({
		default: module.DashboardBlockCard,
	})),
);

function ActionButtons({
	actions,
	onAction,
	disabled,
}: {
	actions: ReadonlyArray<AssistantResponseAction>;
	onAction?: (prompt: string) => void;
	disabled: boolean;
}) {
	if (!onAction || actions.length === 0) return null;
	return (
		<div className={styles.actions}>
			{actions.map((action) => (
				<button
					key={`${action.label}:${action.prompt}`}
					type="button"
					className={action.style === 'primary' ? styles.primaryAction : styles.secondaryAction}
					disabled={disabled}
					onClick={() => onAction(action.prompt)}
					data-flx="konvo-assistant.structured-response.action"
				>
					{action.label}
				</button>
			))}
		</div>
	);
}

function Timeline({
	block,
	onAction,
	disabled,
}: {
	block: AssistantTimelineBlock;
	onAction?: (prompt: string) => void;
	disabled: boolean;
}) {
	const {t} = useLingui();
	const rows = [...block.items].sort((left, right) => clockMinutes(left.start) - clockMinutes(right.start));
	return (
		<section className={styles.schedule} data-flx="konvo-assistant.structured-response.timeline">
			<header className={styles.scheduleHead}>
				<span className={styles.calendarIcon} aria-hidden="true">
					<CalendarBlankIcon size={18} />
				</span>
				<div className={styles.scheduleHeading}>
					<strong>{block.title}</strong>
					{block.subtitle && <span>{block.subtitle}</span>}
				</div>
			</header>
			<div className={styles.timeline}>
				{rows.map((row, index) => (
					<div key={`${row.kind}:${row.start}:${row.end}:${index}`} className={styles.timelineRow}>
						<div className={styles.time}>
							<strong>{row.start}</strong>
							{/* A reminder is a point in time: 08:00 over 08:00 reads as a mistake. */}
							{row.end !== row.start && <span>{row.end}</span>}
						</div>
						<span
							className={row.kind === 'free' ? styles.gapDot : row.tentative ? styles.tentativeDot : styles.eventDot}
							aria-hidden="true"
						/>
						{row.kind === 'free' ? (
							<div className={styles.gap}>
								<span>{row.title || t`Free`}</span>
								<small>{row.meta || readableDuration(rangeMinutes(row.start, row.end))}</small>
							</div>
						) : (
							<div className={styles.event}>
								<strong>{row.title}</strong>
								{row.tentative && <em className={styles.tentativeTag}>{t`Not on your calendar`}</em>}
								{row.meta && <span>{row.meta}</span>}
								{row.note && <p className={styles.eventNote}>{row.note}</p>}
								{row.action && onAction && (
									<button
										type="button"
										className={styles.eventAction}
										disabled={disabled}
										onClick={() => onAction(row.action!.prompt)}
										data-flx="konvo-assistant.structured-response.timeline-action"
									>
										{row.action.label}
									</button>
								)}
							</div>
						)}
					</div>
				))}
			</div>
			{block.actions && block.actions.length > 0 && onAction && (
				<div className={styles.scheduleFoot}>
					<ActionButtons actions={block.actions} onAction={onAction} disabled={disabled} />
				</div>
			)}
		</section>
	);
}

function Carousel({
	block,
	onAction,
	disabled,
}: {
	block: AssistantCarouselBlock;
	onAction?: (prompt: string) => void;
	disabled: boolean;
}) {
	return (
		<section className={styles.carouselBlock} data-flx="konvo-assistant.structured-response.carousel">
			{block.title && <h3 className={styles.blockTitle}>{block.title}</h3>}
			<div className={styles.carousel} role="list" aria-label={block.title}>
				{block.items.map((item) => (
					<article key={item.id} className={styles.carouselItem} role="listitem">
						{item.badge && <span className={styles.itemBadge}>{item.badge}</span>}
						<strong className={styles.carouselTitle}>{item.title}</strong>
						{item.subtitle && <span className={styles.carouselSubtitle}>{item.subtitle}</span>}
						{item.body && (
							<div className={styles.carouselBody}>
								<SafeMarkdown content={item.body} />
							</div>
						)}
						<ActionButtons actions={item.actions ?? []} onAction={onAction} disabled={disabled} />
					</article>
				))}
			</div>
		</section>
	);
}

function Meeting({
	block,
	onAction,
	disabled,
}: {
	block: AssistantMeetingBlock;
	onAction?: (prompt: string) => void;
	disabled: boolean;
}) {
	const {t} = useLingui();
	return (
		<section className={styles.suggestion} data-flx="konvo-assistant.structured-response.meeting">
			<KonvoCardHead
				className={styles.suggestionHeadShared}
				icon={block.phase === 'prepare' ? <SparkleIcon size={16} /> : <CheckSquareIcon size={16} />}
				label={block.phase === 'prepare' ? t`Meeting preparation` : t`Meeting follow-up`}
			/>
			<strong className={styles.suggestionTitle}>{block.title}</strong>
			{(block.when || (block.attendees?.length ?? 0) > 0) && (
				<div className={styles.suggestionMeta}>
					{block.when && (
						<span className={styles.duration}>
							<ClockIcon size={13} />
							{block.when}
						</span>
					)}
					{block.attendees && block.attendees.length > 0 && <span>{block.attendees.join(' · ')}</span>}
				</div>
			)}
			{block.summary && (
				<div className={styles.meetingSummary}>
					<SafeMarkdown content={block.summary} />
				</div>
			)}
			{block.sections && block.sections.length > 0 && (
				<div className={styles.meetingSections}>
					{block.sections.map((section) => (
						<section key={section.title} className={styles.meetingSection}>
							<h4>{section.title}</h4>
							<ul>
								{section.items.map((item, index) => (
									<li key={`${item}:${index}`}>
										<SafeMarkdown content={item} />
									</li>
								))}
							</ul>
						</section>
					))}
				</div>
			)}
			<ActionButtons actions={block.actions ?? []} onAction={onAction} disabled={disabled} />
		</section>
	);
}

export function AssistantStructuredResponse({
	response,
	onAction,
	onSourceOpened,
	disabled = false,
	responseKey,
}: {
	response: AssistantResponse;
	/** `action` names the findings action icon or "choice" (for tracking); other blocks pass only the prompt. */
	onAction?: (prompt: string, action?: string) => void;
	onSourceOpened?: (source: AssistantSourceItem) => void;
	disabled?: boolean;
	/** Stable id of the message carrying this response, so a choice card stays answered. */
	responseKey?: string;
}) {
	return (
		<div className={styles.richReply} data-flx="konvo-assistant.structured-response">
			{response.blocks.map((block, index) => {
				switch (block.type) {
					case 'timeline':
						return <Timeline key={`timeline:${index}`} block={block} onAction={onAction} disabled={disabled} />;
					case 'carousel':
						return <Carousel key={`carousel:${index}`} block={block} onAction={onAction} disabled={disabled} />;
					case 'meeting':
						return <Meeting key={`meeting:${index}`} block={block} onAction={onAction} disabled={disabled} />;
					case 'source':
						return <AssistantSourceCards key={`source:${index}`} block={block} />;
					case 'item':
						return <AssistantItemCard key={`item:${block.id}:${index}`} block={block} />;
					case 'brief':
						return (
							<AssistantBriefCard
								key={`brief:${block.item_id}:${index}`}
								block={block}
								onAction={onAction}
								disabled={disabled}
							/>
						);
					case 'findings':
						return (
							<AssistantFindings
								key={`findings:${index}`}
								block={block}
								onAction={onAction}
								onSourceOpened={onSourceOpened}
								disabled={disabled}
							/>
						);
					case 'choice':
						return (
							<AssistantChoice
								key={`choice:${responseKey ?? ''}:${index}`}
								block={block}
								onAction={onAction}
								disabled={disabled}
								answerKey={responseKey ? `${responseKey}:${index}` : undefined}
							/>
						);
					case 'dashboard':
						return (
							<Suspense
								key={`dashboard:${block.dashboard_id ?? block.share_id}:${index}`}
								fallback={<div className={styles.dashboardLoading} aria-busy="true" />}
							>
								<DashboardBlockCard block={block} onAction={disabled ? undefined : onAction} />
							</Suspense>
						);
					case 'agents':
						return (
							<Suspense key={`agents:${index}`} fallback={null}>
								<AssistantAgentsBlock block={block} />
							</Suspense>
						);
					case 'agent_usage':
						return (
							<Suspense key={`agent-usage:${index}`} fallback={null}>
								<AssistantAgentUsageBlock block={block} />
							</Suspense>
						);
					case 'agents_digest':
						return (
							<Suspense key={`agents-digest:${index}`} fallback={null}>
								<AssistantAgentsDigestBlock block={block} />
							</Suspense>
						);
					case 'wrap_up':
						return (
							<Suspense key={`wrap-up:${block.plan_id}`} fallback={null}>
								<WrapUpCard planId={block.plan_id} where="chat" fallbackTitles={[...block.agents, ...block.tasks]} />
							</Suspense>
						);
				}
			})}
		</div>
	);
}
